import React, { Fragment, createRef } from 'react';
import { Route, Switch, NavLink, withRouter, matchPath, Redirect, BrowserRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import createMuiTheme from '@material-ui/core/styles/createMuiTheme';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';
import MenuIcon from '@material-ui/icons/Menu';

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var objectWithoutProperties = function (obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

/**
 * Helpers for React Router to simplify special handling.
 * Ideas taken from this discussion:
 * https://github.com/ReactTraining/react-router/issues/4105#issuecomment-291834881
 */

/**
 * Subroutine to create a React component with props
 *
 * @param {Object} component    The React component specified in Route
 * @param {Object} routeProps   Props passed by Route component, like 'history'
 * @param {Object} props        An object of props to pass along
 * @param {Object} rest         The rest of the passed props
 * @returns {Function}			A React component
 */
var renderMergedProps = function renderMergedProps(component, routeProps, props) {
  for (var _len = arguments.length, rest = Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
    rest[_key - 3] = arguments[_key];
  }

  var finalProps = Object.assign.apply(Object, [{}].concat(rest, [routeProps, props]));
  // console.log('renderMergedProps', { finalProps, routeProps, props, rest })
  return React.createElement(component, finalProps);
};

/**
 * Special route component that simplifies syntax for passing props
 * to the component specified in Route arguments.
 *
 * Special router props are destructured so are NOT included in 'rest',
 * and therefore not passed on to the route component - cleaner.
 *
 * @constructor
 * @param {Object} component        A React component
 * @param {string} path             All passed props other than component
 * @param {Object} computedMatch    All passed props other than component
 * @param {Object} [children]		Possible child-routes
 * @param {Object} props            Extra props passed AS props.props
 * @param {Object} rest             The rest of the passed props
 * @returns {Object}                Returns a React component WITH PROPS
 */
// eslint-disable-next-line
var PropsRoute = function PropsRoute(_ref) {
  var component = _ref.component,
      path = _ref.path,
      computedMatch = _ref.computedMatch,
      props = _ref.props,
      rest = objectWithoutProperties(_ref, ['component', 'path', 'computedMatch', 'props']);

  // console.log('PropsRoute', { component, path, computedMatch, props, rest })
  // if (!component) debugger

  return React.createElement(Route, _extends({}, rest, {
    render: function render(routeProps) {
      return renderMergedProps(component, routeProps, props, rest);
    }
  }));
};

PropsRoute.propTypes = {
  component: PropTypes.func.isRequired
};

function Home() {
	return React.createElement(
		Fragment,
		null,
		React.createElement(
			Typography,
			{ variant: 'h6', gutterBottom: true },
			'Home'
		),
		React.createElement(
			Typography,
			{ variant: 'subtitle1' },
			'Use the navigation at left to load demos.'
		)
	);
}

var darkTheme = createMuiTheme({
	palette: {
		type: 'dark'
	}
});

var drawerWidth = 200;

// noinspection JSUnresolvedFunction,JSUnresolvedVariable,JSUnresolvedVariable
var styles = function styles(theme) {
	return {
		root: {
			display: 'flex'
		},
		appBar: defineProperty({
			marginLeft: drawerWidth
		}, theme.breakpoints.up('sm'), {
			width: 'calc(100% - ' + drawerWidth + 'px)'
		}),
		drawer: defineProperty({}, theme.breakpoints.up('sm'), {
			width: drawerWidth,
			flexShrink: 0
		}),
		drawerPaper: {
			width: drawerWidth,
			backgroundColor: '#24306b'
		},
		content: {
			flexGrow: 1,
			backgroundColor: theme.palette.background.default,
			padding: theme.spacing.unit * 3
		},
		menuButton: defineProperty({
			marginRight: 20
		}, theme.breakpoints.up('sm'), {
			display: 'none'
		}),
		toolbar: theme.mixins.toolbar,
		topLeftToolbar: _extends({}, theme.mixins.toolbar, {
			backgroundColor: 'rgba(0, 0, 0, 0.54)',
			color: 'white'
		})
	};
};

var navLinkActiveStyle = {
	fontWeight: 'bold',
	color: '#900',
	textDecoration: 'none'
};
var navLinkStyle = {
	textDecoration: 'none'
};

var ListNavItem = withRouter(function (props) {
	var label = props.label,
	    to = props.to,
	    _props$external = props.external,
	    external = _props$external === undefined ? false : _props$external,
	    _props$exact = props.exact,
	    exact = _props$exact === undefined ? false : _props$exact,
	    location = props.location;


	var itemProps = {
		style: navLinkStyle,
		selected: !!matchPath(location.pathname, { path: to, exact: exact })
	};
	if (external) {
		Object.assign(itemProps, {
			href: to,
			component: 'a',
			target: '_blank'
		});
	} else {
		Object.assign(itemProps, {
			to: to,
			exact: exact,
			component: NavLink,
			activeStyle: navLinkActiveStyle
		});
	}

	return React.createElement(
		ListItem,
		_extends({ button: true }, itemProps),
		React.createElement(
			ListItemText,
			null,
			label
		)
	);
});

var arrayOf = PropTypes.arrayOf,
    bool = PropTypes.bool,
    object = PropTypes.object,
    shape = PropTypes.shape,
    string = PropTypes.string;


ListNavItem.propTypes = {
	to: string,
	label: string,
	exact: bool,
	external: bool,
	location: object
};

function DrawerContents(props) {
	var classes = props.classes,
	    pages = props.pages,
	    readme = props.readme,
	    demo = props.demo;


	var useDefaultHomepage = true;
	var _iteratorNormalCompletion = true;
	var _didIteratorError = false;
	var _iteratorError = undefined;

	try {
		for (var _iterator = pages[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
			var page = _step.value;

			if (page.path === '/') {
				useDefaultHomepage = false;
				break;
			}
		}
	} catch (err) {
		_didIteratorError = true;
		_iteratorError = err;
	} finally {
		try {
			if (!_iteratorNormalCompletion && _iterator.return) {
				_iterator.return();
			}
		} finally {
			if (_didIteratorError) {
				throw _iteratorError;
			}
		}
	}

	return React.createElement(
		MuiThemeProvider,
		{ theme: darkTheme },
		React.createElement(CssBaseline, null),
		React.createElement(
			'div',
			{ className: classes.topLeftToolbar },
			React.createElement(
				Toolbar,
				null,
				React.createElement(
					Typography,
					{
						variant: 'subtitle1',
						color: 'inherit',
						noWrap: true
					},
					'Navigation'
				)
			)
		),
		React.createElement(Divider, null),
		React.createElement(
			List,
			null,
			useDefaultHomepage && React.createElement(ListNavItem, {
				label: 'Home',
				to: '/',
				exact: true
			}),
			pages.map(function (_ref, idx) {
				var label = _ref.label,
				    path = _ref.path,
				    exact = _ref.exact,
				    external = _ref.external;

				// No label means DO NOT create a menu item for this route
				if (!label) return null;

				return React.createElement(ListNavItem, {
					key: idx,
					label: label,
					to: path,
					exact: exact || false,
					external: external || false
				});
			})
		),
		(demo || readme) && React.createElement(
			Fragment,
			null,
			React.createElement(Divider, null),
			React.createElement(
				List,
				null,
				readme && React.createElement(ListNavItem, {
					label: 'Readme',
					to: readme,
					external: true
				}),
				demo && React.createElement(ListNavItem, {
					label: 'CodeSandbox Demo',
					to: demo,
					external: true
				})
			)
		),
		React.createElement(Divider, null),
		React.createElement(
			List,
			null,
			React.createElement(ListNavItem, {
				label: 'More Components',
				to: 'https://allpro.github.io/',
				external: true
			})
		)
	);
}

var DemoLayoutApp = function (_React$Component) {
	inherits(DemoLayoutApp, _React$Component);

	function DemoLayoutApp(props) {
		classCallCheck(this, DemoLayoutApp);

		var _this = possibleConstructorReturn(this, (DemoLayoutApp.__proto__ || Object.getPrototypeOf(DemoLayoutApp)).call(this, props));

		_this.state = {
			openDrawer: false

			// Use wrapper as container for Drawer so works well inside CodeSandbox
		};_this.containerRef = createRef();

		_this.toggleDrawer = _this.toggleDrawer.bind(_this);
		return _this;
	}

	createClass(DemoLayoutApp, [{
		key: 'toggleDrawer',
		value: function toggleDrawer() {
			this.setState(function (state) {
				return { openDrawer: !state.openDrawer };
			});
		}
	}, {
		key: 'render',
		value: function render() {
			var appProps = this.props;
			var classes = appProps.classes,
			    pages = appProps.pages;


			var useDefaultHomepage = true;
			var _iteratorNormalCompletion2 = true;
			var _didIteratorError2 = false;
			var _iteratorError2 = undefined;

			try {
				for (var _iterator2 = pages[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
					var page = _step2.value;

					if (page.path === '/') {
						useDefaultHomepage = false;
						break;
					}
				}
			} catch (err) {
				_didIteratorError2 = true;
				_iteratorError2 = err;
			} finally {
				try {
					if (!_iteratorNormalCompletion2 && _iterator2.return) {
						_iterator2.return();
					}
				} finally {
					if (_didIteratorError2) {
						throw _iteratorError2;
					}
				}
			}

			return React.createElement(
				'div',
				{ className: classes.root, ref: this.containerRef },
				React.createElement(CssBaseline, null),
				React.createElement(
					AppBar,
					{ position: 'fixed', className: classes.appBar },
					React.createElement(
						Toolbar,
						null,
						React.createElement(
							IconButton,
							{
								color: 'inherit',
								'aria-label': 'Open drawer',
								onClick: this.toggleDrawer,
								className: classes.menuButton
							},
							React.createElement(MenuIcon, null)
						),
						React.createElement(
							Typography,
							{ variant: 'h6', color: 'inherit', noWrap: true },
							appProps.title || 'Demo Title'
						)
					)
				),
				React.createElement(
					'nav',
					{ className: classes.drawer },
					React.createElement(
						Hidden,
						{ smUp: true, implementation: 'css' },
						React.createElement(
							Drawer,
							{
								variant: 'temporary',
								container: this.containerRef.current,
								anchor: 'left',
								open: this.state.openDrawer,
								onClose: this.toggleDrawer,
								classes: { paper: classes.drawerPaper }
							},
							React.createElement(DrawerContents, appProps)
						)
					),
					React.createElement(
						Hidden,
						{ xsDown: true, implementation: 'css' },
						React.createElement(
							Drawer,
							{
								variant: 'permanent',
								open: true,
								classes: {
									paper: classes.drawerPaper
								}
							},
							React.createElement(DrawerContents, appProps)
						)
					)
				),
				React.createElement(
					'main',
					{ id: 'top', className: classes.content },
					React.createElement('div', { className: classes.toolbar }),
					React.createElement(
						Switch,
						null,
						useDefaultHomepage && React.createElement(PropsRoute, {
							path: '/',
							exact: true,
							component: Home
						}),
						pages.map(function (_ref2, idx) {
							var path = _ref2.path,
							    exact = _ref2.exact,
							    component = _ref2.component,
							    props = _ref2.props;
							return React.createElement(PropsRoute, {
								key: idx,
								path: path,
								exact: exact,
								component: component,
								props: props
							});
						})
					)
				)
			);
		}
	}]);
	return DemoLayoutApp;
}(React.Component);

DemoLayoutApp.propTypes = {
	classes: object.isRequired,
	pages: arrayOf(shape({
		label: string,
		path: string,
		exact: bool,
		external: bool
	})).isRequired,
	title: string,
	readme: string,
	demo: string
};

var App = withStyles(styles)(DemoLayoutApp);

// noinspection JSUnresolvedVariable
var isGitHubPages = /(github\.io|codesandbox\.io)/.test(window.location.hostname);

function DemoLayout(_ref) {
	var packageName = _ref.packageName,
	    pages = _ref.pages,
	    rest = objectWithoutProperties(_ref, ['packageName', 'pages']);

	return React.createElement(
		BrowserRouter,
		{ basename: isGitHubPages && packageName ? '/' + packageName : '' },
		React.createElement(
			Switch,
			null,
			pages.map(function (_ref2, idx) {
				var path = _ref2.path,
				    exact = _ref2.exact,
				    props = _ref2.props;
				return React.createElement(PropsRoute, _extends({
					key: idx,
					path: path,
					exact: exact,
					component: App,
					props: props,
					pages: pages
				}, rest));
			}),
			React.createElement(PropsRoute, _extends({
				path: '/',
				exact: true,
				component: App,
				pages: pages
			}, rest)),
			React.createElement(Redirect, { to: '/' })
		)
	);
}

export default DemoLayout;



//////////////////
// WEBPACK FOOTER
// /mnt/c/Users/kevin/OneDrive/DEV/GitHub/demo-layout/esm/index.js
// module id = 144
// module chunks = 0